import type { UniqueIdentifier } from '@dnd-kit/core'
import type { AnimateLayoutChanges } from '@dnd-kit/sortable'
import { defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type {
    ActorEvent,
    ButtonItem,
    Field,
    FieldCellValue,
    FieldValue,
    RecordLikeProtocol,
    RichTextContentProtocol,
    ViewField
} from '@lighthouse/core'
import type { FC } from 'react'
import React from 'react'

import { KanbanBoardColumn } from './Column'
import type { SortItemsDataWithKey } from './utils'

const animateLayoutChanges: AnimateLayoutChanges = args => defaultAnimateLayoutChanges({ ...args, wasDragging: true })

const TYPE = 'container'

interface Props {
    disabled?: boolean
    enableHidden?: boolean
    id: UniqueIdentifier
    list: SortItemsDataWithKey[]
    blockId: string
    schema: Record<string, Field>
    columns: ViewField[]
    fieldSchema: Field
    groupedFieldValue?: FieldCellValue
    collapsed?: boolean
    creatable?: boolean
    handleCollapse?: () => void
    handleHidden?: () => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRenderButtonTitle: (v: RichTextContentProtocol, record?: RecordLikeProtocol) => string
    handleClickAdd?: (content?: Record<string, FieldValue>) => Promise<RecordLikeProtocol | undefined>
}

export const DroppableContainer: FC<Props> = ({
    id,
    list,
    blockId,
    disabled,
    enableHidden,
    schema,
    columns,
    fieldSchema,
    groupedFieldValue,
    collapsed,
    creatable,
    handleCollapse,
    handleHidden,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderButtonTitle,
    handleClickAdd
}) => {
    const items = list.map(item => item.key)
    const { active, attributes, isDragging, listeners, over, setNodeRef, setActivatorNodeRef, transition, transform } = useSortable({
        id,
        disabled,
        data: { type: TYPE, children: items },
        animateLayoutChanges
    })

    const isOverContainer = over ? (over.id === id && active?.data.current?.type !== TYPE) || items.includes(over.id as string) : false

    return (
        <KanbanBoardColumn
            blockId={blockId}
            ref={disabled ? undefined : setNodeRef}
            style={{
                transition,
                transform: CSS.Translate.toString(transform),
                opacity: isDragging ? 0.5 : undefined
            }}
            disabled={disabled}
            enableHidden={enableHidden}
            handleProps={{ ...attributes, ...listeners, ref: setActivatorNodeRef }}
            isOverContainer={isOverContainer}
            list={list}
            schema={schema}
            columns={columns}
            groupedFieldValue={groupedFieldValue}
            fieldSchema={fieldSchema}
            collapsed={collapsed}
            creatable={creatable}
            handleCollapse={handleCollapse}
            handleHidden={handleHidden}
            onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
            onRecordClick={onRecordClick}
            onRenderButtonTitle={onRenderButtonTitle}
            handleClickAdd={handleClickAdd}
        />
    )
}
